import React, { useState, useEffect, useRef, useCallback } from 'react';
import { submitJob } from '../apiClient';

// The 12 AI Capability Classes
const CAPABILITY_CLASSES = [
  { id: 1, name: 'Machine Learning', short: 'ML', description: 'Pattern recognition & classification' },
  { id: 2, name: 'Computer Vision', short: 'CV', description: 'Image & video analysis' },
  { id: 3, name: 'Generative AI', short: 'GenAI', description: 'Text generation & drafts' },
  { id: 4, name: 'Predictive Analytics', short: 'PA', description: 'Forecasting & trend analysis' },
  { id: 5, name: 'Natural Language Processing (NLP)', short: 'NLP', description: 'Text analysis & sentiment' },
  { id: 6, name: 'Speech & Voice', short: 'SV', description: 'Speech-to-text & voice control' },
  { id: 7, name: 'Optimization & Planning', short: 'OP', description: 'Resource planning & scheduling' },
  { id: 8, name: 'Recommendation', short: 'RP', description: 'Recommendations & personalization' },
  { id: 9, name: 'Conversational AI', short: 'CAI', description: 'Chatbots & assistants' },
  { id: 10, name: 'Robotics', short: 'ROB', description: 'Physical automation' },
  { id: 11, name: 'Robotic Process Automation', short: 'RPA', description: 'Rule-based digital automation' },
  { id: 12, name: 'Document Intelligence', short: 'DI', description: 'Form & document extraction' },
];

export default function MappingLoading({ processData, processContext, onComplete }) {
  const [scanIndex, setScanIndex] = useState(-1);
  const [mappingResult, setMappingResult] = useState(null);
  const [error, setError] = useState(null);
  const [apiDone, setApiDone] = useState(false);
  const [finished, setFinished] = useState(false);

  const scanTimer = useRef(null);
  const [attempt, setAttempt] = useState(0);

  const handleRetry = useCallback(() => {
    setError(null);
    setMappingResult(null);
    setApiDone(false);
    setFinished(false);
    setScanIndex(0);
    setAttempt((a) => a + 1);
  }, []);

  // Step 1: Call the API in parallel with animation
  useEffect(() => {
    if (!processData?.steps) return;

    let cancelled = false;
    const fetchMapping = async () => {
      try {
        const data = await submitJob(
          '/api/map-capabilities',
          {
            steps: processData.steps,
            processContext: typeof processContext === 'string'
              ? processContext
              : JSON.stringify(processContext),
          },
          '/api/map-capabilities/jobs',
          { isCancelled: () => cancelled }
        );
        if (cancelled || !data) return;
        setMappingResult(data);
        setApiDone(true);
      } catch (err) {
        if (cancelled) return;
        console.error('Mapping error:', err);
        setError(err.message);
      }
    };

    fetchMapping();
    setScanIndex(0);
    return () => { cancelled = true; };
  }, [processData, processContext, attempt]);

  // Step 2: Scanning animation — loops continuously through all 12 classes
  // at 0.6s per tile until the API is done. Progress bar grows slowly.
  useEffect(() => {
    if (scanIndex < 0) return;
    if (finished) return;

    // If API is done, mark finished and transition
    if (apiDone && mappingResult) {
      setFinished(true);
      setTimeout(() => onComplete(mappingResult), 600);
      return;
    }

    // Advance to next tile (wrapping around with modulo for continuous loop)
    scanTimer.current = setTimeout(() => {
      setScanIndex(prev => prev + 1);
    }, 600);

    return () => clearTimeout(scanTimer.current);
  }, [scanIndex, apiDone, mappingResult, onComplete, finished]);

  // Current tile position (loops through 0-11 continuously)
  const currentTileIndex = scanIndex >= 0 ? scanIndex % CAPABILITY_CLASSES.length : -1;

  // Progress bar: grows slowly based on elapsed tiles, caps at 90% until finished
  const progressPercent = finished
    ? 100
    : Math.min(90, Math.round(Math.min(scanIndex + 1, 40) / 40 * 90));
  const currentClassName = currentTileIndex >= 0
    ? CAPABILITY_CLASSES[currentTileIndex].name
    : '';

  if (error) {
    return (
      <div className="mapping-container">
        <div className="mapping-loading-center">
          <div className="error-message">Fehler beim Capability Mapping: {error}</div>
          <button className="btn btn-generate" onClick={handleRetry}>
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mapping-container">
      <div className="mapping-window">
        {/* Header in German (6.5.2) */}
        <div className="mapping-header">
          <h2>KI-Fähigkeiten werden analysiert...</h2>
          <p>
            {processData?.steps?.length || 0} Prozessschritte werden gegen 12 KI-Klassen gescannt
          </p>
        </div>

        {/* Capability Grid — 4x3 */}
        <div className="capability-grid">
          {CAPABILITY_CLASSES.map((cap, i) => {
            const isCurrentTile = currentTileIndex === i;

            let stateClass = 'idle';
            if (finished) stateClass = 'scanned';
            else if (isCurrentTile) stateClass = 'scanning';

            return (
              <div key={cap.id} className={`capability-tile ${stateClass}`}>
                <div className="tile-short">{cap.short}</div>
                <div className="tile-name">{cap.name}</div>
                <div className="tile-desc">{cap.description}</div>
                {isCurrentTile && !finished && <div className="tile-scan-line" />}
              </div>
            );
          })}
        </div>

        {/* Current scan label */}
        <div className="scan-status">
          {finished
            ? 'Analyse abgeschlossen'
            : currentTileIndex >= 0
              ? `Scanne: ${currentClassName}...`
              : 'Starte Analyse...'}
        </div>

        {/* Progress bar */}
        <div className="scan-progress-bar">
          <div className="scan-progress-fill" style={{ width: `${progressPercent}%` }} />
        </div>
        <div className="scan-progress-label">{progressPercent}%</div>
      </div>
    </div>
  );
}
